import { useState } from "react";

import { Input } from "@monorepo-apps/ui/src/components/input";
import { Label } from "@monorepo-apps/ui/src/components/label";
import { Textarea } from "@monorepo-apps/ui/src/components/textarea";

import type {
  CoverageFilter,
  CriterionDerivationKind,
  CriterionGroup,
  EvaluationCriterionRow,
} from "@/infrastructure/supabase/database.types";
import { CRITERION_GROUP_LABEL, CRITERION_GROUP_ORDER } from "@/presentation/data/criteria";

export interface CriterionFormValues {
  slug: string;
  name: string;
  description: string | null;
  rubric: string | null;
  weight_default: number;
  is_dealbreaker_eligible: boolean;
  display_order: number;
  derivation_kind: CriterionDerivationKind;
  coverage_filter: CoverageFilter | null;
  group_key: CriterionGroup;
}

interface CriterionFormBodyProps {
  initial: EvaluationCriterionRow | null;
  onChange: (values: CriterionFormValues) => void;
}

const DEFAULT_VALUES: CriterionFormValues = {
  slug: "",
  name: "",
  description: null,
  rubric: null,
  weight_default: 0.05,
  is_dealbreaker_eligible: false,
  display_order: 100,
  derivation_kind: "manual",
  coverage_filter: null,
  group_key: "execution",
};

export function CriterionFormBody({ initial, onChange }: CriterionFormBodyProps) {
  const [values, setValues] = useState<CriterionFormValues>(
    initial
      ? {
          slug: initial.slug,
          name: initial.name,
          description: initial.description,
          rubric: initial.rubric,
          weight_default: initial.weight_default,
          is_dealbreaker_eligible: initial.is_dealbreaker_eligible,
          display_order: initial.display_order,
          derivation_kind: initial.derivation_kind,
          coverage_filter: initial.coverage_filter,
          group_key: initial.group_key,
        }
      : DEFAULT_VALUES
  );

  const update = (patch: Partial<CriterionFormValues>) => {
    const next = { ...values, ...patch };
    setValues(next);
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-[1.4fr_1fr_90px] gap-3">
        <Field label="Slug (stable identifier)">
          <Input
            value={values.slug}
            onChange={(e) =>
              update({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })
            }
            placeholder="my-new-criterion"
            disabled={initial !== null}
          />
        </Field>
        <Field label="Group">
          <select
            value={values.group_key}
            onChange={(e) => update({ group_key: e.target.value as CriterionGroup })}
            className="h-9 w-full rounded-md border bg-transparent px-3 text-sm"
          >
            {CRITERION_GROUP_ORDER.map((g) => (
              <option key={g} value={g}>
                {CRITERION_GROUP_LABEL[g]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Display order">
          <Input
            type="number"
            value={values.display_order}
            onChange={(e) => update({ display_order: Number(e.target.value) })}
          />
        </Field>
      </div>

      <Field label="Name">
        <Input
          value={values.name}
          onChange={(e) => update({ name: e.target.value })}
          placeholder="Human-readable label"
        />
      </Field>

      <Field label="Description">
        <Textarea
          rows={2}
          value={values.description ?? ""}
          onChange={(e) => update({ description: e.target.value || null })}
          placeholder="One sentence on what this criterion captures."
        />
      </Field>

      <Field label="Rubric (markdown — 0..5 anchors)">
        <Textarea
          rows={4}
          value={values.rubric ?? ""}
          onChange={(e) => update({ rubric: e.target.value || null })}
          placeholder="- **0** — …&#10;- **5** — …"
          className="font-data text-[12px]"
        />
      </Field>

      <div className="grid grid-cols-3 gap-3">
        <Field label={`Default weight (${(values.weight_default * 100).toFixed(0)}%)`}>
          <Input
            type="number"
            step="0.01"
            min="0"
            max="1"
            value={values.weight_default}
            onChange={(e) =>
              update({ weight_default: Math.max(0, Math.min(1, Number(e.target.value))) })
            }
          />
        </Field>
        <Field label="Dealbreaker-eligible">
          <select
            value={values.is_dealbreaker_eligible ? "yes" : "no"}
            onChange={(e) => update({ is_dealbreaker_eligible: e.target.value === "yes" })}
            className="h-9 w-full rounded-md border bg-transparent px-3 text-sm"
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        </Field>
        <Field label="Derivation">
          <select
            value={values.derivation_kind}
            onChange={(e) => update({ derivation_kind: e.target.value as CriterionDerivationKind })}
            className="h-9 w-full rounded-md border bg-transparent px-3 text-sm"
          >
            <option value="manual">Manual (score entered)</option>
            <option value="derived-coverage">Derived (auto from coverage)</option>
          </select>
        </Field>
      </div>

      {values.derivation_kind === "derived-coverage" ? (
        <Field label="Coverage filter (JSON)">
          <Textarea
            rows={3}
            value={values.coverage_filter ? JSON.stringify(values.coverage_filter, null, 2) : ""}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw.trim().length === 0) {
                update({ coverage_filter: null });
                return;
              }
              try {
                update({ coverage_filter: JSON.parse(raw) as CoverageFilter });
              } catch {
                // Keep invalid as-is in the textarea but don't push to parent
              }
            }}
            placeholder='{"journey":["B2C","both"],"exclude_triage":["wont-do"]}'
            className="font-data text-[12px]"
          />
        </Field>
      ) : null}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-foreground/80 text-[11.5px] font-medium">{label}</Label>
      {children}
    </div>
  );
}
